export const BED_CROSSFADE_S = 2;
export const BED_LOOP_S = 10;
export const WIND_BED_GAIN = 0.09;
export const SURF_BED_GAIN = 0.11;
export const NIGHT_BED_GAIN = 0.075;
export const FIRE_BED_GAIN = 0.085;
export const PULSE_BED_GAIN = 0.11;
export const CUE_AMB_CRASH_WIND = 'aud_amb_crash_wind';
export const CUE_AMB_SURF = 'aud_amb_surf';
export const CUE_AMB_NIGHT = 'aud_amb_night';
export const CUE_AMB_FIRE = 'aud_amb_fire';
export const CUE_AMB_PULSE = 'aud_amb_pulse';
export const CUE_SFX_DILOPH_TELL = 'aud_sfx_diloph_tell';
export const CUE_SFX_DILOPH_SPIT = 'aud_sfx_diloph_spit';
export const CUE_SFX_SPEAR = 'aud_sfx_spear';
export const CUE_SFX_DEATH = 'aud_sfx_death';
export const CUE_SFX_PULSE = 'aud_sfx_pulse_fall';

/**
 * M-SIGNAL: E–B–F♯–C as a narrow heterodyne, never a beep melody.
 * Pulse weather uses a falling minor second (E3→D♯3), not this contour,
 * so the ear can tell Harrow weather from home. Pulse carrier is E–B–F–C
 * with C late or missing. Night is E Phrygian, missing third (no G).
 */
export const M_SIGNAL_HZ = [329.6276, 493.8833, 369.9944, 523.2511] as const;
export const M_SIGNAL_PULSE_HZ = [329.6276, 493.8833, 349.2282, 523.2511] as const;
export const HARROW_FALL_HZ = [164.8138, 155.5635] as const;

const buffers = new WeakMap<BaseAudioContext, Map<string, AudioBuffer>>();

function cached(ctx: BaseAudioContext, key: string, build: () => AudioBuffer): AudioBuffer {
  let map = buffers.get(ctx);
  if (!map) {
    map = new Map();
    buffers.set(ctx, map);
  }
  const hit = map.get(key);
  if (hit) {
    return hit;
  }
  const buf = build();
  map.set(key, buf);
  return buf;
}

/** Warm critical-path buffers on the one AudioContext. */
export function warmCriticalBeds(ctx: BaseAudioContext): void {
  synthesizeCrashWind(ctx);
  synthesizeSurf(ctx);
  synthesizeNightBed(ctx);
  synthesizeFireBed(ctx);
  synthesizePulseBed(ctx);
  synthesizePulseStinger(ctx);
  synthesizeDilophTell(ctx);
  synthesizeDilophSpit(ctx);
  synthesizeSpearSwing(ctx);
  synthesizeDeathVeil(ctx);
}

/** High-mid Crash Coast rustle. Filtered noise, not a sine. */
export function synthesizeCrashWind(ctx: BaseAudioContext, seconds = BED_LOOP_S): AudioBuffer {
  return cached(ctx, `wind:${seconds}`, () => buildCrashWind(ctx, seconds));
}

function buildCrashWind(ctx: BaseAudioContext, seconds: number): AudioBuffer {
  const sr = ctx.sampleRate;
  const n = Math.max(1, Math.floor(sr * Math.max(seconds, 0.05)));
  const buffer = ctx.createBuffer(1, n, sr);
  const out = buffer.getChannelData(0);
  const airLp = onePole(3600, sr, false);
  const airHp = onePole(520, sr, true);
  const rustleLp = onePole(5400, sr, false);
  const rustleHp = onePole(1400, sr, true);
  let seed = 0xc0a57;
  let pink = 0;

  for (let i = 0; i < n; i++) {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    const white = (seed / 0xffffffff) * 2 - 1;
    pink = pink * 0.84 + white * 0.16;
    const gust = 0.74 + irregularWalk(i, sr, 0x51ed, 0.11) * 0.26;
    out[i] = airLp(airHp(pink)) * 0.58 * gust;
    const rustle = rustleLp(rustleHp(white));
    const grain = irregularWalk(i, sr, 0x5eaf, 0.08);
    if (grain > 0.68) {
      out[i] = (out[i] ?? 0) + rustle * (grain - 0.68) * 0.62;
    }
  }

  paintBurst(out, sr, 0.62, 0.11, { lo: 900, hi: 2800, gain: 0.18, seed: 0xa101 });
  paintBurst(out, sr, 1.84, 0.09, { lo: 1100, hi: 3200, gain: 0.14, seed: 0xa202 });
  paintBurst(out, sr, 3.15, 0.13, { lo: 800, hi: 2400, gain: 0.16, seed: 0xa303 });
  paintBurst(out, sr, 4.4, 0.08, { lo: 1300, hi: 3600, gain: 0.12, seed: 0xa404 });
  paintBurst(out, sr, 5.72, 0.15, { lo: 700, hi: 2200, gain: 0.17, seed: 0xa505 });
  paintBurst(out, sr, 7.05, 0.1, { lo: 1000, hi: 3000, gain: 0.13, seed: 0xa606 });
  paintBurst(out, sr, 8.38, 0.12, { lo: 850, hi: 2600, gain: 0.15, seed: 0xa707 });
  paintBurst(out, sr, 9.21, 0.08, { lo: 1400, hi: 3800, gain: 0.11, seed: 0xa808 });
  seam(out, sr, 0.12);
  tame(out, 0.16);
  return buffer;
}

/** Low surf rumble. Slower filtered noise, not a sine. */
export function synthesizeSurf(ctx: BaseAudioContext, seconds = BED_LOOP_S): AudioBuffer {
  return cached(ctx, `surf:${seconds}`, () => buildSurf(ctx, seconds));
}

function buildSurf(ctx: BaseAudioContext, seconds: number): AudioBuffer {
  const sr = ctx.sampleRate;
  const n = Math.max(1, Math.floor(sr * Math.max(seconds, 0.05)));
  const buffer = ctx.createBuffer(1, n, sr);
  const out = buffer.getChannelData(0);
  const bodyLp = onePole(180, sr, false);
  const bodyHp = onePole(38, sr, true);
  const foamLp = onePole(720, sr, false);
  const foamHp = onePole(180, sr, true);
  let seed = 0x5eaf11;
  let brown = 0;

  for (let i = 0; i < n; i++) {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    const white = (seed / 0xffffffff) * 2 - 1;
    brown = (brown + white * 0.018) / 1.018;
    const swell = 0.7 + irregularWalk(i, sr, 0x2c01, 0.34) * 0.3;
    out[i] = bodyLp(bodyHp(brown)) * 0.92 * swell;
    const foamGate = irregularWalk(i, sr, 0x77ab, 0.28);
    if (foamGate > 0.74) {
      out[i] = (out[i] ?? 0) + foamLp(foamHp(white)) * (foamGate - 0.74) * 0.28;
    }
  }

  paintBurst(out, sr, 1.1, 0.85, { lo: 40, hi: 160, gain: 0.28, seed: 0xb101 });
  paintBurst(out, sr, 3.6, 1.1, { lo: 32, hi: 140, gain: 0.32, seed: 0xb202 });
  paintBurst(out, sr, 6.4, 0.9, { lo: 36, hi: 150, gain: 0.26, seed: 0xb303 });
  paintBurst(out, sr, 8.7, 0.7, { lo: 44, hi: 170, gain: 0.22, seed: 0xb404 });
  seam(out, sr, 0.14);
  tame(out, 0.15);
  return buffer;
}

/** Night: E Phrygian drone (no third) + insects. M-SIGNAL as faint heterodyne, C missing. */
export function synthesizeNightBed(ctx: BaseAudioContext, seconds = BED_LOOP_S): AudioBuffer {
  return cached(ctx, `night:${seconds}`, () => {
    const sr = ctx.sampleRate;
    const n = Math.max(1, Math.floor(sr * Math.max(seconds, 0.05)));
    const buffer = ctx.createBuffer(1, n, sr);
    const out = buffer.getChannelData(0);
    const droneE = onePole(92, sr, false);
    const droneEhp = onePole(46, sr, true);
    const droneB = onePole(72, sr, false);
    const droneBhp = onePole(38, sr, true);
    const insectHp = onePole(2600, sr, true);
    const insectLp = onePole(7400, sr, false);
    const resE = resonator(M_SIGNAL_HZ[0], 16, sr);
    const resB = resonator(M_SIGNAL_HZ[1], 16, sr);
    const resFs = resonator(M_SIGNAL_HZ[2], 16, sr);
    let seed = 0x1e07a1;
    let brown = 0;
    let pink = 0;

    for (let i = 0; i < n; i++) {
      seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
      const white = (seed / 0xffffffff) * 2 - 1;
      brown = (brown + white * 0.02) / 1.02;
      pink = pink * 0.86 + white * 0.14;
      const breathe = 0.76 + irregularWalk(i, sr, 0x40d1, 0.46) * 0.24;
      out[i] = droneE(droneEhp(brown)) * 0.72 * breathe + droneB(droneBhp(brown)) * 0.34;
      const insectGate = irregularWalk(i, sr, 0x1c77, 0.065);
      if (insectGate > 0.6) {
        out[i] = (out[i] ?? 0) + insectLp(insectHp(white)) * (insectGate - 0.6) * 0.52;
      }
      const grain = irregularWalk(i, sr, 0x51e2, 0.52);
      if (grain > 0.84) {
        const pick = (seed >>> 8) % 3;
        const res = pick === 0 ? resE : pick === 1 ? resB : resFs;
        out[i] = (out[i] ?? 0) + res(pink) * 0.045;
      }
    }

    paintBurst(out, sr, 0.4, 0.05, { lo: 3200, hi: 6800, gain: 0.16, seed: 0xc101 });
    paintBurst(out, sr, 1.55, 0.04, { lo: 2800, hi: 6200, gain: 0.14, seed: 0xc202 });
    paintBurst(out, sr, 2.9, 0.06, { lo: 3400, hi: 7200, gain: 0.15, seed: 0xc303 });
    paintBurst(out, sr, 4.2, 0.045, { lo: 3000, hi: 6400, gain: 0.13, seed: 0xc404 });
    paintBurst(out, sr, 5.85, 0.055, { lo: 2600, hi: 6000, gain: 0.15, seed: 0xc505 });
    paintBurst(out, sr, 7.4, 0.04, { lo: 3500, hi: 7500, gain: 0.12, seed: 0xc606 });
    paintBurst(out, sr, 8.7, 0.05, { lo: 2900, hi: 6600, gain: 0.14, seed: 0xc707 });
    seam(out, sr, 0.16);
    tame(out, 0.15);
    return buffer;
  });
}

/** Camp Ember crackle. Low rumble + irregular pops, not a tone. */
export function synthesizeFireBed(ctx: BaseAudioContext, seconds = 8): AudioBuffer {
  return cached(ctx, `fire:${seconds}`, () => {
    const sr = ctx.sampleRate;
    const n = Math.max(1, Math.floor(sr * Math.max(seconds, 0.05)));
    const buffer = ctx.createBuffer(1, n, sr);
    const out = buffer.getChannelData(0);
    const rumbleLp = onePole(160, sr, false);
    const rumbleHp = onePole(36, sr, true);
    const hissLp = onePole(3800, sr, false);
    const hissHp = onePole(900, sr, true);
    let seed = 0xf1a4e;
    let brown = 0;

    for (let i = 0; i < n; i++) {
      seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
      const white = (seed / 0xffffffff) * 2 - 1;
      brown = (brown + white * 0.022) / 1.022;
      const glow = 0.7 + irregularWalk(i, sr, 0x8e01, 0.22) * 0.3;
      out[i] = rumbleLp(rumbleHp(brown)) * 0.85 * glow;
      const hissGate = irregularWalk(i, sr, 0x33c0, 0.09);
      if (hissGate > 0.58) {
        out[i] = (out[i] ?? 0) + hissLp(hissHp(white)) * (hissGate - 0.58) * 0.22;
      }
    }

    paintBurst(out, sr, 0.35, 0.035, { lo: 900, hi: 3400, gain: 0.28, seed: 0xd101 });
    paintBurst(out, sr, 1.12, 0.028, { lo: 1200, hi: 4200, gain: 0.24, seed: 0xd202 });
    paintBurst(out, sr, 1.88, 0.04, { lo: 700, hi: 2800, gain: 0.3, seed: 0xd303 });
    paintBurst(out, sr, 2.64, 0.022, { lo: 1600, hi: 4800, gain: 0.2, seed: 0xd404 });
    paintBurst(out, sr, 3.4, 0.038, { lo: 800, hi: 3000, gain: 0.26, seed: 0xd505 });
    paintBurst(out, sr, 4.15, 0.03, { lo: 1100, hi: 3600, gain: 0.22, seed: 0xd606 });
    paintBurst(out, sr, 5.02, 0.045, { lo: 600, hi: 2400, gain: 0.32, seed: 0xd707 });
    paintBurst(out, sr, 5.9, 0.025, { lo: 1400, hi: 4000, gain: 0.21, seed: 0xd808 });
    paintBurst(out, sr, 6.7, 0.033, { lo: 950, hi: 3200, gain: 0.25, seed: 0xd909 });
    paintBurst(out, sr, 7.45, 0.029, { lo: 1300, hi: 3800, gain: 0.23, seed: 0xda0a });
    seam(out, sr, 0.1);
    tame(out, 0.16);
    return buffer;
  });
}

/** Pulse bed: insects cut. Lattice ticks + Pulse carrier E–B–F, C late. */
export function synthesizePulseBed(ctx: BaseAudioContext, seconds = BED_LOOP_S): AudioBuffer {
  return cached(ctx, `pulse-bed:${seconds}`, () => {
    const sr = ctx.sampleRate;
    const n = Math.max(1, Math.floor(sr * Math.max(seconds, 0.05)));
    const buffer = ctx.createBuffer(1, n, sr);
    const out = buffer.getChannelData(0);
    const bodyLp = onePole(110, sr, false);
    const bodyHp = onePole(28, sr, true);
    const resE = resonator(M_SIGNAL_PULSE_HZ[0], 18, sr);
    const resB = resonator(M_SIGNAL_PULSE_HZ[1], 18, sr);
    const resF = resonator(M_SIGNAL_PULSE_HZ[2], 18, sr);
    let seed = 0x7a110;
    let brown = 0;

    for (let i = 0; i < n; i++) {
      seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
      const white = (seed / 0xffffffff) * 2 - 1;
      brown = (brown + white * 0.016) / 1.016;
      const press = 0.8 + irregularWalk(i, sr, 0x90aa, 0.6) * 0.2;
      out[i] = bodyLp(bodyHp(brown)) * 0.9 * press;
      const t = i / sr;
      if (t > 1.1 && t < 1.22) {
        out[i] = (out[i] ?? 0) + resE(white) * 0.06;
      } else if (t > 2.4 && t < 2.52) {
        out[i] = (out[i] ?? 0) + resB(white) * 0.05;
      } else if (t > 3.85 && t < 3.97) {
        out[i] = (out[i] ?? 0) + resF(white) * 0.045;
      }
    }

    paintBurst(out, sr, 0.55, 0.018, { lo: 2200, hi: 5600, gain: 0.22, seed: 0xe101 });
    paintBurst(out, sr, 1.7, 0.016, { lo: 2400, hi: 6000, gain: 0.2, seed: 0xe202 });
    paintBurst(out, sr, 2.95, 0.02, { lo: 2000, hi: 5200, gain: 0.24, seed: 0xe303 });
    paintBurst(out, sr, 4.2, 0.015, { lo: 2600, hi: 6200, gain: 0.18, seed: 0xe404 });
    paintBurst(out, sr, 5.55, 0.019, { lo: 2100, hi: 5400, gain: 0.21, seed: 0xe505 });
    paintBurst(out, sr, 6.9, 0.017, { lo: 2300, hi: 5800, gain: 0.19, seed: 0xe606 });
    paintBurst(out, sr, 8.15, 0.016, { lo: 2500, hi: 6100, gain: 0.17, seed: 0xe707 });
    paintBurst(out, sr, 8.62, 0.08, { lo: M_SIGNAL_PULSE_HZ[3] * 0.9, hi: M_SIGNAL_PULSE_HZ[3] * 1.1, gain: 0.05, seed: 0xe808 });
    seam(out, sr, 0.14);
    tame(out, 0.14);
    return buffer;
  });
}

/** Harrow onset: falling minor second, not M-SIGNAL. Lattice in the tail. */
export function synthesizePulseStinger(ctx: BaseAudioContext, seconds = 1.2): AudioBuffer {
  return cached(ctx, `pulse-fall:${seconds}`, () => {
    const sr = ctx.sampleRate;
    const n = Math.max(1, Math.floor(sr * Math.max(seconds, 0.05)));
    const buffer = ctx.createBuffer(1, n, sr);
    const out = buffer.getChannelData(0);
    const loA = onePole(HARROW_FALL_HZ[0] * 0.72, sr, true);
    const hiA = onePole(HARROW_FALL_HZ[0] * 1.28, sr, false);
    const loB = onePole(HARROW_FALL_HZ[1] * 0.72, sr, true);
    const hiB = onePole(HARROW_FALL_HZ[1] * 1.28, sr, false);
    const bodyLp = onePole(220, sr, false);
    let seed = 0x5a11;

    for (let i = 0; i < n; i++) {
      seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
      const white = (seed / 0xffffffff) * 2 - 1;
      const u = i / Math.max(1, n - 1);
      const env = u < 0.08 ? u / 0.08 : Math.max(0, 1 - (u - 0.08) / 0.92);
      const fall = u < 0.38 ? 1 : Math.max(0, 1 - (u - 0.38) / 0.22);
      const land = u < 0.32 ? 0 : Math.min(1, (u - 0.32) / 0.2);
      const a = hiA(loA(white));
      const b = hiB(loB(white));
      out[i] = (a * fall + b * land) * env * 0.7 + bodyLp(white) * env * 0.22;
    }

    paintBurst(out, sr, 0.18, 0.02, { lo: 2000, hi: 5400, gain: 0.2, seed: 0xf101 });
    paintBurst(out, sr, 0.52, 0.016, { lo: 2400, hi: 6000, gain: 0.16, seed: 0xf202 });
    paintBurst(out, sr, 0.88, 0.018, { lo: 1800, hi: 4800, gain: 0.14, seed: 0xf303 });
    tame(out, 0.2);
    return buffer;
  });
}

/** Diloph tell: wet hiss + throat, positional one-shot. */
export function synthesizeDilophTell(ctx: BaseAudioContext, seconds = 0.58): AudioBuffer {
  return cached(ctx, `diloph-tell:${seconds}`, () => {
    const sr = ctx.sampleRate;
    const n = Math.max(1, Math.floor(sr * Math.max(seconds, 0.05)));
    const buffer = ctx.createBuffer(1, n, sr);
    const out = buffer.getChannelData(0);
    const throatHp = onePole(280, sr, true);
    const throatLp = onePole(980, sr, false);
    const hissHp = onePole(1800, sr, true);
    const hissLp = onePole(6200, sr, false);
    let seed = 0xd110;
    let pink = 0;

    for (let i = 0; i < n; i++) {
      seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
      const white = (seed / 0xffffffff) * 2 - 1;
      pink = pink * 0.82 + white * 0.18;
      const u = i / Math.max(1, n - 1);
      const env = u < 0.16 ? u / 0.16 : Math.max(0, 1 - (u - 0.16) / 0.84);
      const flutter = 0.7 + irregularWalk(i, sr, 0xaa01, 0.03) * 0.3;
      out[i] = throatLp(throatHp(pink)) * env * 0.55 + hissLp(hissHp(white)) * env * 0.5 * flutter;
    }

    paintBurst(out, sr, 0.08, 0.03, { lo: 700, hi: 1600, gain: 0.2, seed: 0x1101 });
    paintBurst(out, sr, 0.22, 0.025, { lo: 900, hi: 2200, gain: 0.16, seed: 0x1202 });
    paintBurst(out, sr, 0.38, 0.028, { lo: 600, hi: 1800, gain: 0.18, seed: 0x1303 });
    tame(out, 0.22);
    return buffer;
  });
}

/** Diloph spit: wet spray + splat. */
export function synthesizeDilophSpit(ctx: BaseAudioContext, seconds = 0.42): AudioBuffer {
  return cached(ctx, `diloph-spit:${seconds}`, () => {
    const sr = ctx.sampleRate;
    const n = Math.max(1, Math.floor(sr * Math.max(seconds, 0.05)));
    const buffer = ctx.createBuffer(1, n, sr);
    const out = buffer.getChannelData(0);
    const sprayHp = onePole(900, sr, true);
    const sprayLp = onePole(7800, sr, false);
    const splatLp = onePole(240, sr, false);
    const splatHp = onePole(40, sr, true);
    let seed = 0x5b17;
    let brown = 0;

    for (let i = 0; i < n; i++) {
      seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
      const white = (seed / 0xffffffff) * 2 - 1;
      brown = (brown + white * 0.03) / 1.03;
      const u = i / Math.max(1, n - 1);
      const spray = u < 0.22 ? u / 0.22 : Math.max(0, 1 - (u - 0.22) / 0.55);
      const splat = u < 0.12 ? 0 : u < 0.28 ? (u - 0.12) / 0.16 : Math.max(0, 1 - (u - 0.28) / 0.5);
      out[i] = sprayLp(sprayHp(white)) * spray * 0.62 + splatLp(splatHp(brown)) * splat * 0.7;
    }

    paintBurst(out, sr, 0.14, 0.05, { lo: 200, hi: 900, gain: 0.28, seed: 0x2101 });
    tame(out, 0.24);
    return buffer;
  });
}

/** Spear: wood shaft + stone tip + air whoosh. Two layers, not a beep. */
export function synthesizeSpearSwing(ctx: BaseAudioContext, seconds = 0.24): AudioBuffer {
  return cached(ctx, `spear:${seconds}`, () => {
    const sr = ctx.sampleRate;
    const n = Math.max(1, Math.floor(sr * Math.max(seconds, 0.05)));
    const buffer = ctx.createBuffer(1, n, sr);
    const out = buffer.getChannelData(0);
    const whooshHp = onePole(420, sr, true);
    const whooshLp = onePole(2400, sr, false);
    const woodHp = onePole(70, sr, true);
    const woodLp = onePole(380, sr, false);
    const stoneHp = onePole(2400, sr, true);
    const stoneLp = onePole(7200, sr, false);
    let seed = 0x5eaf;
    let brown = 0;

    for (let i = 0; i < n; i++) {
      seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
      const white = (seed / 0xffffffff) * 2 - 1;
      brown = (brown + white * 0.025) / 1.025;
      const u = i / Math.max(1, n - 1);
      const air = u < 0.2 ? u / 0.2 : Math.max(0, 1 - (u - 0.2) / 0.8);
      const wood = u < 0.08 ? 0 : u < 0.2 ? (u - 0.08) / 0.12 : Math.max(0, 1 - (u - 0.2) / 0.35);
      const stone = u < 0.14 ? 0 : u < 0.2 ? (u - 0.14) / 0.06 : Math.max(0, 1 - (u - 0.2) / 0.18);
      out[i] =
        whooshLp(whooshHp(white)) * air * 0.42 +
        woodLp(woodHp(brown)) * wood * 0.7 +
        stoneLp(stoneHp(white)) * stone * 0.38;
    }

    tame(out, 0.22);
    return buffer;
  });
}

/** Death veil: analog wash. M-SIGNAL E–B–F♯, C never arrives. */
export function synthesizeDeathVeil(ctx: BaseAudioContext, seconds = 2.4): AudioBuffer {
  return cached(ctx, `death:${seconds}`, () => {
    const sr = ctx.sampleRate;
    const n = Math.max(1, Math.floor(sr * Math.max(seconds, 0.05)));
    const buffer = ctx.createBuffer(1, n, sr);
    const out = buffer.getChannelData(0);
    const darkLp = onePole(180, sr, false);
    const darkHp = onePole(30, sr, true);
    const airLp = onePole(1400, sr, false);
    const airHp = onePole(200, sr, true);
    const resE = resonator(M_SIGNAL_HZ[0], 12, sr);
    const resB = resonator(M_SIGNAL_HZ[1], 12, sr);
    const resFs = resonator(M_SIGNAL_HZ[2], 12, sr);
    let seed = 0x0ead;
    let brown = 0;
    let pink = 0;

    for (let i = 0; i < n; i++) {
      seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
      const white = (seed / 0xffffffff) * 2 - 1;
      brown = (brown + white * 0.02) / 1.02;
      pink = pink * 0.88 + white * 0.12;
      const u = i / Math.max(1, n - 1);
      const env = u < 0.12 ? u / 0.12 : Math.max(0, 1 - (u - 0.12) / 0.88);
      const wow = 0.72 + irregularWalk(i, sr, 0x7e0, 0.18) * 0.28;
      const bright = Math.max(0, 1 - u * 0.85);
      out[i] = darkLp(darkHp(brown)) * env * 0.8 * wow + airLp(airHp(pink)) * env * 0.28 * bright;
      const t = i / sr;
      if (t > 0.22 && t < 0.4) {
        out[i] = (out[i] ?? 0) + resE(pink) * env * 0.07;
      } else if (t > 0.72 && t < 0.9) {
        out[i] = (out[i] ?? 0) + resB(pink) * env * 0.055;
      } else if (t > 1.28 && t < 1.46) {
        out[i] = (out[i] ?? 0) + resFs(pink) * env * 0.04;
      }
    }

    tame(out, 0.18);
    return buffer;
  });
}

function resonator(freq: number, q: number, sr: number): (x: number) => number {
  const f = Math.max(20, Math.min(freq, sr * 0.45));
  const w = (2 * Math.PI * f) / sr;
  const r = Math.exp(-((Math.PI * f) / (Math.max(1, q) * sr)));
  const a1 = -2 * r * Math.cos(w);
  const a2 = r * r;
  const inputGain = 1 - r;
  let y1 = 0;
  let y2 = 0;
  return (x: number) => {
    const y = inputGain * x - a1 * y1 - a2 * y2;
    y2 = y1;
    y1 = y;
    return y;
  };
}

function paintBurst(
  out: Float32Array,
  sr: number,
  start: number,
  duration: number,
  spec: { lo: number; hi: number; gain: number; seed: number },
): void {
  const i0 = Math.floor(start * sr);
  const i1 = Math.min(out.length, Math.floor((start + duration) * sr));
  if (i1 <= i0) {
    return;
  }
  const hp = onePole(spec.lo, sr, true);
  const lp = onePole(spec.hi, sr, false);
  let seed = spec.seed >>> 0;
  for (let i = i0; i < i1; i++) {
    const u = (i - i0) / Math.max(1, i1 - i0);
    const env = u < 0.18 ? u / 0.18 : Math.max(0, 1 - (u - 0.18) / 0.82);
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    const noise = (seed / 0xffffffff) * 2 - 1;
    const index = i;
    out[index] = (out[index] ?? 0) + lp(hp(noise)) * env * spec.gain;
  }
}

/** Slow irregular walk. Not a sine LFO. */
function irregularWalk(i: number, sr: number, seed0: number, stepS = 0.09): number {
  const step = Math.max(1, Math.floor(sr * stepS));
  const cell = Math.floor(i / step);
  let a = (Math.imul(cell, 1664525) + seed0) >>> 0;
  a = (Math.imul(a, 1664525) + 1013904223) >>> 0;
  let b = (Math.imul(cell + 1, 1664525) + seed0) >>> 0;
  b = (Math.imul(b, 1664525) + 1013904223) >>> 0;
  const u0 = a / 0xffffffff;
  const u1 = b / 0xffffffff;
  const t = (i % step) / step;
  const s = t * t * (3 - 2 * t);
  return u0 * (1 - s) + u1 * s;
}

function seam(out: Float32Array, sr: number, fadeS: number): void {
  const fade = Math.min(out.length >> 1, Math.max(1, Math.floor(sr * fadeS)));
  for (let i = 0; i < fade; i++) {
    const w = i / fade;
    const head = out[i] ?? 0;
    const tail = out[out.length - fade + i] ?? 0;
    const mixed = tail * (1 - w) + head * w;
    out[i] = mixed;
    out[out.length - fade + i] = mixed;
  }
}

function tame(out: Float32Array, peak: number): void {
  let max = 0;
  for (let i = 0; i < out.length; i++) {
    max = Math.max(max, Math.abs(out[i] ?? 0));
  }
  if (max < 1e-8) {
    return;
  }
  const g = peak / max;
  for (let i = 0; i < out.length; i++) {
    const index = i;
    out[index] = (out[index] ?? 0) * g;
  }
}

function onePole(freq: number, sr: number, highpass: boolean): (x: number) => number {
  const x = Math.exp((-2 * Math.PI * freq) / sr);
  let prevX = 0;
  let prevY = 0;
  return (sample: number) => {
    const y = highpass ? x * (prevY + sample - prevX) : sample * (1 - x) + prevY * x;
    prevX = sample;
    prevY = y;
    return y;
  };
}
